import {
	EventIdentifier,
	TrackingEventType,
	useAnalytics,
	useIntl,
	useSettings,
	WCPledgeTabNames
} from '@capsicum/common';
import { type ItemTabProps, useRouter, useSearchParam } from '@capsicum/common-web';
import { useCallback, useEffect, useMemo } from 'react';

import { WCPledgeActivityTab, WCPledgeGuideTab, WCPledgeLeaderboardTab, WCPledgeTokensTab } from '../feature/tabs';

export const useWCPledgeTabs = () => {
	const { shallowReplaceSearchParam } = useRouter();
	const { $t } = useIntl();
	const analytics = useAnalytics();
	const {
		settings: { lockerRoomInternationalsConfig }
	} = useSettings();
	const showWCLockerRoomLearboardTab = lockerRoomInternationalsConfig.showWCLockerRoomLearboardTab;

	const PledgeTabs: Array<ItemTabProps> = useMemo(
		() =>
			[
				{
					component: () => WCPledgeTokensTab(),
					id: WCPledgeTabNames.Tokens,
					text: $t('worldChampionshipPledge.tabs.tokens')
				},
				{
					component: () => WCPledgeLeaderboardTab(),
					id: WCPledgeTabNames.Leaderboard,
					text: $t('worldChampionshipPledge.tabs.leaderboard')
				},
				//TODO: Uncomment this and put it under a flag if necessary
				// {
				// 	component: () => WCPledgeBundlesTab(),
				// 	text: $t('worldChampionshipPledge.tabs.bundles'),
				// 	id: WCPledgeTabNames.Bundles
				// },
				{
					component: () => WCPledgeActivityTab(),
					id: WCPledgeTabNames.Activity,
					text: $t('worldChampionshipPledge.tabs.activity')
				},
				{
					component: () => WCPledgeGuideTab(),
					id: WCPledgeTabNames.Guide,
					text: $t('worldChampionshipPledge.tabs.guide')
				}
			].filter(tab => (tab.id === WCPledgeTabNames.Leaderboard ? showWCLockerRoomLearboardTab : true)),
		[]
	);

	const setActiveTab = useCallback(
		(name: string) => {
			shallowReplaceSearchParam('tab', name);
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
				action: `wc_pledge_${name}_view`
			});
			window.scrollTo({ top: 0 });
		},
		[shallowReplaceSearchParam]
	);

	const tabParam = useSearchParam('tab');
	const { push } = useRouter();

	// Redirect if trying to access leaderboard tab but config is false
	useEffect(() => {
		if (tabParam === WCPledgeTabNames.Leaderboard && !showWCLockerRoomLearboardTab) {
			// Redirect to default tab if leaderboard is not enabled
			push('/locker-room-internationals?tab=tokens');
		}
	}, [tabParam, showWCLockerRoomLearboardTab, push]);

	const activeTab: WCPledgeTabNames = useMemo(() => {
		// If trying to access leaderboard but it's disabled, default to Tokens
		if (tabParam === WCPledgeTabNames.Leaderboard && !showWCLockerRoomLearboardTab) {
			return WCPledgeTabNames.Tokens;
		}

		return Object.values(WCPledgeTabNames).includes(tabParam as WCPledgeTabNames)
			? (tabParam as WCPledgeTabNames)
			: WCPledgeTabNames.Tokens;
	}, [tabParam, showWCLockerRoomLearboardTab]);

	const showBasketFooter = activeTab === WCPledgeTabNames.Tokens;

	return {
		activeTab,
		setActiveTab,
		showBasketFooter,
		tabs: PledgeTabs
	};
};
